import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, Mail, Users, BarChart2, Plus, Pencil, Trash2, Eye, Copy,
  Upload, Search, X, Check, Loader2, MousePointerClick,
  AlertCircle, Inbox, RefreshCw, FileText,
  Clock, CalendarClock, Zap, Target, Activity, Ban, Repeat, PauseCircle, PlayCircle,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { toast } from 'sonner';
import api from '../../api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

// ── helpers ────────────────────────────────────────────────────────────────────
const CAT_COLORS = {
  announcement: '#8B5CF6', newsletter: '#0891B2', onboarding: '#10B981',
  reminder: '#F59E0B', promotional: '#EF4444', custom: '#64748B',
  schools: '#2563eb', parents: '#EC4899', teachers: '#14B8A6',
  students: '#F97316', partners: '#6366F1',
};
const catColor = (c) => CAT_COLORS[c] || '#64748B';

function CatBadge({ cat }) {
  const color = catColor(cat);
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: `${color}18`, color }}>
      {cat}
    </span>
  );
}

function StatusBadge({ status, scheduledAt }) {
  const map = {
    draft:     { cls: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Draft' },
    sending:   { cls: 'bg-blue-50 text-blue-700 border-blue-100', label: 'Sending…' },
    sent:      { cls: 'bg-emerald-50 text-emerald-700 border-emerald-100', label: 'Sent' },
    failed:    { cls: 'bg-red-50 text-red-700 border-red-100', label: 'Failed' },
    scheduled: { cls: 'bg-violet-50 text-violet-700 border-violet-100', label: 'Scheduled' },
  };
  const { cls, label } = map[status] || map.draft;
  if (status === 'scheduled' && scheduledAt) {
    const dt = new Date(scheduledAt);
    const fmt = dt.toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
        <Clock className="w-3 h-3" />{fmt}
      </span>
    );
  }
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>{label}</span>;
}

// Premium input class
const INPUT = 'w-full border border-slate-200 bg-white rounded-xl px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';

const DAYS_OF_WEEK = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const HOURS = Array.from({length:24},(_,i)=>`${String(i).padStart(2,'0')}:00 UTC`);

function RecurrenceBadge({ type, day, hour, count, paused }) {
  if (!type || type === 'none') return null;
  const label = type === 'weekly'
    ? `Every ${DAYS_OF_WEEK[day] || 'week'}`
    : `Monthly on day ${day}`;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${paused ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-violet-50 text-violet-700 border-violet-100'}`}>
      {paused ? <PauseCircle className="w-3 h-3" /> : <Repeat className="w-3 h-3" />}
      {paused ? 'Paused' : label}
      {count > 0 && !paused && <span className="text-violet-400">·{count}×</span>}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TEMPLATES TAB
// ══════════════════════════════════════════════════════════════════════════════
function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'announcement', subject: '', preview_text: '', html: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/marketing/templates'); setTemplates(r.data || []); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ name: '', category: 'announcement', subject: '', preview_text: '', html: '' }); setEditModal('new'); };
  const openEdit = (t) => { setForm({ name: t.name, category: t.category, subject: t.subject, preview_text: t.preview_text || '', html: t.html }); setEditModal(t); };
  const openDuplicate = async (t) => {
    await api.post('/marketing/templates', { ...t, name: `${t.name} (Copy)`, is_default: false });
    toast.success('Template duplicated'); load();
  };
  const handleSave = async () => {
    if (!form.name || !form.subject || !form.html) { toast.error('Name, subject and HTML are required'); return; }
    setSaving(true);
    try {
      if (editModal === 'new') { await api.post('/marketing/templates', form); toast.success('Template created'); }
      else { await api.put(`/marketing/templates/${editModal.id}`, form); toast.success('Template updated'); }
      setEditModal(null); load();
    } finally { setSaving(false); }
  };
  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await api.delete(`/marketing/templates/${t.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.detail || 'Cannot delete built-in template'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Email Templates</h2>
          <p className="text-sm text-slate-500 mt-0.5">{templates.length} templates · Inbox-deliverable HTML</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 px-5 py-2.5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-sm font-semibold rounded-xl shadow-sm transition-all hover:shadow-blue-200 hover:shadow-lg" data-testid="new-template-btn">
          <Plus className="w-4 h-4" /> New Template
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(4)].map((_, i) => <div key={i} className="h-52 bg-slate-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {templates.map(t => (
            <div key={t.id} className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden group shadow-[0_2px_8px_-2px_rgba(15,23,42,0.06)] hover:shadow-[0_12px_24px_-4px_rgba(15,23,42,0.1)] transition-all" data-testid={`template-card-${t.id}`}>
              <div className="h-44 bg-slate-50 relative border-b border-slate-100 overflow-hidden cursor-pointer" onClick={() => setPreviewModal(t)}>
                <iframe srcDoc={t.html} sandbox="allow-same-origin" title={t.name}
                  className="w-full pointer-events-none origin-top-left"
                  style={{ height: '600px', width: '220%', transform: 'scale(0.45)', marginLeft: '-55%', marginTop: '-2%' }} />
                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/10 transition-all flex items-center justify-center">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2 text-xs font-semibold text-slate-700 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all translate-y-1 group-hover:translate-y-0 shadow-sm">
                    <Eye className="w-3.5 h-3.5" /> Preview
                  </div>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900 truncate text-sm">{t.name}</p>
                    <p className="text-xs text-slate-400 truncate mt-0.5">{t.subject}</p>
                    <div className="mt-2"><CatBadge cat={t.category} /></div>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button onClick={() => openEdit(t)} className="p-1.5 text-slate-400 hover:text-[#2563eb] hover:bg-blue-50 rounded-lg transition-colors" data-testid={`edit-template-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => openDuplicate(t)} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"><Copy className="w-3.5 h-3.5" /></button>
                    {!t.is_default && <button onClick={() => handleDelete(t)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button onClick={openNew} className="h-52 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center gap-2.5 text-slate-400 hover:border-[#2563eb] hover:text-[#2563eb] hover:bg-blue-50/50 transition-all group">
            <div className="w-10 h-10 rounded-xl bg-slate-100 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
              <Plus className="w-5 h-5" />
            </div>
            <span className="text-sm font-semibold">New Template</span>
          </button>
        </div>
      )}

      {/* Preview Modal */}
      <Dialog open={!!previewModal} onOpenChange={() => setPreviewModal(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden rounded-3xl">
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-slate-100">
            <DialogTitle className="font-bold text-slate-900">{previewModal?.name}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[70vh]">
            <iframe srcDoc={previewModal?.html} sandbox="allow-same-origin" title="preview" className="w-full" style={{ height: '600px' }} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit/Create Modal */}
      <Dialog open={!!editModal} onOpenChange={() => setEditModal(null)}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden rounded-3xl" style={{ height: '90vh' }}>
          <div className="flex h-full">
            {/* Left sidebar */}
            <div className="w-80 flex-shrink-0 bg-slate-50 border-r border-slate-200 flex flex-col overflow-y-auto">
              <div className="p-6 border-b border-slate-200">
                <h3 className="font-bold text-slate-900 text-base">{editModal === 'new' ? 'New Template' : 'Edit Template'}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{editModal !== 'new' && editModal?.name}</p>
              </div>
              <div className="p-6 space-y-4 flex-1">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">Template Name *</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={INPUT} placeholder="e.g. Welcome Email" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">Category</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className={INPUT + ' bg-white cursor-pointer'}>
                    {['announcement','newsletter','onboarding','reminder','promotional','custom'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">Email Subject *</label>
                  <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className={INPUT} placeholder="Email subject line" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">Preview Text</label>
                  <input value={form.preview_text} onChange={e => setForm(f => ({ ...f, preview_text: e.target.value }))} className={INPUT} placeholder="Inbox preview snippet" />
                </div>
                <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
                  <p className="text-xs font-bold text-blue-700 mb-2 uppercase tracking-wide">Template Variables</p>
                  <div className="space-y-1.5">
                    {['{{name}}','{{first_name}}','{{email}}','{{unsubscribe_link}}'].map(v => (
                      <code key={v} className="block text-xs text-blue-600 bg-white/80 rounded-lg px-2.5 py-1 font-mono border border-blue-100">{v}</code>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
                  <p className="text-xs font-bold text-amber-700 mb-2 uppercase tracking-wide">Inbox Tips</p>
                  <ul className="text-xs text-amber-700 space-y-1.5">
                    {['Use inline CSS only','Table-based layout','Subject under 50 chars','Include unsubscribe link'].map(t => (
                      <li key={t} className="flex items-start gap-1.5"><Check className="w-3 h-3 mt-0.5 flex-shrink-0" />{t}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="p-6 border-t border-slate-200">
                <button onClick={handleSave} disabled={saving} className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white font-semibold text-sm py-3 rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all" data-testid="save-template-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save Template
                </button>
              </div>
            </div>
            {/* Right: split editor/preview */}
            <div className="flex-1 flex overflow-hidden">
              <div className="flex-1 flex flex-col border-r border-slate-200">
                <div className="px-4 py-3 bg-[#0f172a] flex items-center gap-2 border-b border-slate-700">
                  <FileText className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">HTML Editor</span>
                </div>
                <textarea value={form.html} onChange={e => setForm(f => ({ ...f, html: e.target.value }))}
                  className="flex-1 font-mono text-xs p-5 resize-none outline-none text-slate-300 bg-[#0f172a] leading-relaxed"
                  placeholder="Paste your HTML email here..." spellCheck={false} data-testid="template-html-editor" />
              </div>
              <div className="flex-1 flex flex-col">
                <div className="px-4 py-3 bg-slate-50 flex items-center gap-2 border-b border-slate-200">
                  <Eye className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Live Preview</span>
                </div>
                <iframe srcDoc={form.html || '<body style="font-family:sans-serif;color:#94a3b8;display:flex;align-items:center;justify-content:center;height:200px;margin:0;"><p style="margin:auto">Start typing HTML to preview</p></body>'}
                  sandbox="allow-same-origin" title="live-preview" className="flex-1 bg-white" />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CONTACTS TAB
// ══════════════════════════════════════════════════════════════════════════════
function ContactsTab() {
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [listModal, setListModal] = useState(null);
  const [addContactModal, setAddContactModal] = useState(false);
  const [listForm, setListForm] = useState({ name: '', category: 'schools', description: '' });
  const [contactForm, setContactForm] = useState({ email: '', name: '' });
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const csvRef = useRef(null);
  const CATEGORIES = ['schools', 'parents', 'teachers', 'students', 'partners', 'custom'];

  const loadLists = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/marketing/contacts/lists'); setLists(r.data || []); } finally { setLoading(false); }
  }, []);

  const loadContacts = useCallback(async (listId, q = '') => {
    if (!listId) return;
    setLoadingContacts(true);
    try {
      const r = await api.get(`/marketing/contacts/lists/${listId}/contacts?limit=200&q=${q}`);
      setContacts(r.data || []); setTotalContacts(r.total || 0);
    } finally { setLoadingContacts(false); }
  }, []);

  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => { if (selectedList) loadContacts(selectedList.id, search); }, [selectedList, search, loadContacts]);

  const handleSaveList = async () => {
    if (!listForm.name.trim()) { toast.error('List name required'); return; }
    setSaving(true);
    try {
      if (listModal === 'new') { await api.post('/marketing/contacts/lists', listForm); toast.success('List created'); }
      else { await api.put(`/marketing/contacts/lists/${listModal.id}`, listForm); toast.success('Updated'); }
      setListModal(null); loadLists();
    } finally { setSaving(false); }
  };

  const handleDeleteList = async (lst) => {
    if (!window.confirm(`Delete "${lst.name}" and all its contacts?`)) return;
    await api.delete(`/marketing/contacts/lists/${lst.id}`); toast.success('Deleted');
    if (selectedList?.id === lst.id) setSelectedList(null);
    loadLists();
  };

  const handleAddContact = async () => {
    if (!contactForm.email) { toast.error('Email required'); return; }
    setSaving(true);
    try {
      await api.post(`/marketing/contacts/lists/${selectedList.id}/contacts`, contactForm);
      toast.success('Contact added'); setAddContactModal(false); setContactForm({ email: '', name: '' });
      loadContacts(selectedList.id); loadLists();
    } catch (e) { toast.error(e.detail || 'Failed'); } finally { setSaving(false); }
  };

  const handleDeleteContact = async (c) => {
    await api.delete(`/marketing/contacts/lists/${selectedList.id}/contacts/${c.id}`);
    loadContacts(selectedList.id, search); loadLists();
  };

  const handleCSV = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const lines = ev.target.result.split('\n').filter(Boolean);
      const isHeader = lines[0]?.toLowerCase().includes('email');
      const rows = (isHeader ? lines.slice(1) : lines).map(line => {
        const [email, name] = line.split(',').map(s => s.trim().replace(/^"|"$/g, ''));
        return { email, name: name || '' };
      }).filter(r => r.email?.includes('@'));
      if (!rows.length) { toast.error('No valid emails found'); return; }
      const r = await api.post(`/marketing/contacts/lists/${selectedList.id}/import`, { rows });
      toast.success(`Imported ${r.inserted} contacts (${r.skipped} skipped)`);
      loadContacts(selectedList.id); loadLists();
    };
    reader.readAsText(file); e.target.value = '';
  };

  return (
    <div className="flex gap-6" style={{ minHeight: 500, maxHeight: 'calc(100vh - 260px)' }}>
      {/* Left list sidebar */}
      <div className="w-72 flex-shrink-0 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-slate-900">Contact Lists</p>
          <button onClick={() => { setListForm({ name: '', category: 'schools', description: '' }); setListModal('new'); }}
            className="w-8 h-8 bg-[#2563eb] text-white rounded-lg flex items-center justify-center hover:bg-[#1d4ed8] transition-colors shadow-sm" data-testid="new-list-btn">
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? [...Array(3)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />) :
            lists.map(lst => (
              <div key={lst.id} onClick={() => setSelectedList(lst)}
                className={`flex items-center gap-3 p-3.5 rounded-xl cursor-pointer transition-all group border ${selectedList?.id === lst.id ? 'border-[#2563eb]/30 bg-blue-50 shadow-sm' : 'border-slate-200/60 bg-white hover:border-slate-300 hover:bg-slate-50'}`}
                data-testid={`contact-list-${lst.id}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${catColor(lst.category)}18` }}>
                  <Users className="w-4 h-4" style={{ color: catColor(lst.category) }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{lst.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{(lst.contact_count || 0).toLocaleString()} contacts</p>
                </div>
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity">
                  <button onClick={e => { e.stopPropagation(); setListForm({ name: lst.name, category: lst.category, description: lst.description || '' }); setListModal(lst); }}
                    className="p-1 text-slate-400 hover:text-[#2563eb] rounded-md"><Pencil className="w-3 h-3" /></button>
                  <button onClick={e => { e.stopPropagation(); handleDeleteList(lst); }}
                    className="p-1 text-slate-400 hover:text-red-500 rounded-md"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))
          }
          {!loading && lists.length === 0 && (
            <div className="text-center py-10 text-slate-400">
              <Users className="w-8 h-8 mx-auto mb-2 text-slate-200" />
              <p className="text-sm">No lists yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Right contacts area */}
      <div className="flex-1 flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-[0_2px_8px_-2px_rgba(15,23,42,0.06)]">
        {!selectedList ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-400 p-8">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
              <Users className="w-8 h-8 text-slate-300" />
            </div>
            <p className="text-sm font-semibold">Select a list to view contacts</p>
            <p className="text-xs text-slate-400">Choose a contact list from the sidebar</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <p className="font-bold text-slate-900">{selectedList.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{totalContacts.toLocaleString()} contacts</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts…"
                    className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all w-44" />
                </div>
                <button onClick={() => csvRef.current?.click()} className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors" data-testid="import-csv-btn">
                  <Upload className="w-3 h-3" /> Import CSV
                </button>
                <button onClick={() => { setContactForm({ email: '', name: '' }); setAddContactModal(true); }}
                  className="flex items-center gap-1.5 px-3 py-2 bg-[#2563eb] text-white rounded-lg text-xs font-semibold hover:bg-[#1d4ed8] transition-colors shadow-sm" data-testid="add-contact-btn">
                  <Plus className="w-3 h-3" /> Add
                </button>
                <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={handleCSV} />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingContacts ? (
                <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300 mx-auto" /></div>
              ) : contacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-2">
                  <Inbox className="w-10 h-10 text-slate-200" />
                  <p className="text-sm font-medium">No contacts yet</p>
                  <p className="text-xs">Import a CSV or add manually</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50/90 backdrop-blur-sm z-10">
                    <tr>
                      {['Name', 'Email', 'Status', ''].map(h => (
                        <th key={h} className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide border-b border-slate-100">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {contacts.map(c => (
                      <tr key={c.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-[#2563eb]/10 flex items-center justify-center text-xs font-bold text-[#2563eb] flex-shrink-0">
                              {(c.name || c.email)[0]?.toUpperCase()}
                            </div>
                            <span className="font-semibold text-slate-800">{c.name || '—'}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-slate-500">{c.email}</td>
                        <td className="px-5 py-3">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${c.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-red-700 border-red-100'}`}>{c.status}</span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button onClick={() => handleDeleteContact(c)} className="p-1.5 text-slate-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {/* List modal */}
      <Dialog open={!!listModal} onOpenChange={() => setListModal(null)}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader><DialogTitle className="font-bold">{listModal === 'new' ? 'Create Contact List' : 'Edit List'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">List Name *</label>
              <input value={listForm.name} onChange={e => setListForm(f => ({ ...f, name: e.target.value }))} className={INPUT} placeholder="e.g. School Directors Lagos" data-testid="list-name-input" /></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">Category</label>
              <select value={listForm.category} onChange={e => setListForm(f => ({ ...f, category: e.target.value }))} className={INPUT + ' bg-white'}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">Description</label>
              <textarea value={listForm.description} onChange={e => setListForm(f => ({ ...f, description: e.target.value }))} className={INPUT + ' resize-none h-16'} placeholder="Optional description" /></div>
            <button onClick={handleSaveList} disabled={saving} className="w-full bg-[#2563eb] text-white py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-[#1d4ed8] transition-colors shadow-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save List
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add contact modal */}
      <Dialog open={addContactModal} onOpenChange={setAddContactModal}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader><DialogTitle className="font-bold">Add Contact</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">Email *</label>
              <input type="email" value={contactForm.email} onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} className={INPUT} placeholder="email@example.com" /></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">Name</label>
              <input value={contactForm.name} onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))} className={INPUT} placeholder="Full name (optional)" /></div>
            <button onClick={handleAddContact} disabled={saving} className="w-full bg-[#2563eb] text-white py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-[#1d4ed8] transition-colors shadow-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Contact
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CAMPAIGNS TAB
// ══════════════════════════════════════════════════════════════════════════════
function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({
    name: '', subject: '', template_id: '', list_ids: [],
    from_name: 'EDUVA Team', from_email: 'noreply@app.myeduva.com',
    send_mode: 'now', scheduled_at: '',
    recurrence_type: 'none', recurrence_day: 1, recurrence_hour: 9,
  });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState({});
  const [duplicating, setDuplicating] = useState({});
  const [cancelling, setCancelling] = useState({});
  const [pausing, setPausing] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);

  const load = useCallback(async (overridePage) => {
    const p = overridePage ?? currentPage;
    setLoading(true);
    try {
      const [cr, tr, lr] = await Promise.all([
        api.get(`/marketing/campaigns?page=${p}&limit=10`),
        api.get('/marketing/templates'),
        api.get('/marketing/contacts/lists'),
      ]);
      setCampaigns(cr.data || []);
      setTotalPages(cr.total_pages || 1);
      setTotalCampaigns(cr.total || 0);
      if (overridePage !== undefined) setCurrentPage(overridePage);
      setTemplates(tr.data || []); setLists(lr.data || []);
    } finally { setLoading(false); }
  }, [currentPage]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name || !form.subject || !form.template_id || !form.list_ids.length) {
      toast.error('Fill all required fields'); return;
    }
    if (form.send_mode === 'schedule' && !form.scheduled_at) {
      toast.error('Please pick a scheduled date and time'); return;
    }
    const payload = {
      name: form.name, subject: form.subject, template_id: form.template_id,
      list_ids: form.list_ids, from_name: form.from_name,
      scheduled_at: form.send_mode === 'schedule' ? form.scheduled_at : null,
      recurrence_type: form.recurrence_type,
      recurrence_day:  Number(form.recurrence_day),
      recurrence_hour: Number(form.recurrence_hour),
    };
    setSaving(true);
    try {
      await api.post('/marketing/campaigns', payload);
      toast.success(form.send_mode === 'schedule' ? 'Campaign scheduled!' : 'Campaign created');
      setCreateModal(false); load(1);
    } finally { setSaving(false); }
  };

  const handleSend = async (c) => {
    if (!window.confirm(`Send "${c.name}" to contacts in ${c.list_ids?.length || 0} list(s) right now?`)) return;
    setSending(s => ({ ...s, [c.id]: true }));
    try {
      await api.post(`/marketing/campaigns/${c.id}/send`, {});
      toast.success('Campaign sending started!');
      setTimeout(() => { load(); setSending(s => ({ ...s, [c.id]: false })); }, 2000);
    } catch (e) { toast.error(e.detail || 'Send failed'); setSending(s => ({ ...s, [c.id]: false })); }
  };

  const handleDuplicate = async (c) => {
    setDuplicating(d => ({ ...d, [c.id]: true }));
    try {
      const res = await api.post(`/marketing/campaigns/${c.id}/duplicate`, {});
      if (res?.success) { toast.success(`"${c.name}" duplicated as draft`); load(); }
    } catch { toast.error('Failed to duplicate'); }
    setDuplicating(d => ({ ...d, [c.id]: false }));
  };

  const handleCancelSchedule = async (c) => {
    if (!window.confirm(`Cancel schedule for "${c.name}"? It will revert to a draft.`)) return;
    setCancelling(s => ({ ...s, [c.id]: true }));
    try {
      await api.post(`/marketing/campaigns/${c.id}/cancel-schedule`, {});
      toast.success('Schedule cancelled — campaign is now a draft');
      load();
    } catch (e) { toast.error(e.detail || 'Failed'); }
    setCancelling(s => ({ ...s, [c.id]: false }));
  };

  const handlePauseRecurrence = async (c) => {
    setPausing(p => ({ ...p, [c.id]: true }));
    try {
      const res = await api.post(`/marketing/campaigns/${c.id}/pause-recurrence`, {});
      toast.success(res?.message || 'Updated');
      load();
    } catch (e) { toast.error(e.detail || 'Failed'); }
    setPausing(p => ({ ...p, [c.id]: false }));
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    await api.delete(`/marketing/campaigns/${c.id}`); toast.success('Deleted'); load();
  };

  const toggleListId = (id) => setForm(f => ({
    ...f, list_ids: f.list_ids.includes(id) ? f.list_ids.filter(x => x !== id) : [...f.list_ids, id]
  }));

  const minSchedule = () => new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Campaigns</h2>
          <p className="text-sm text-slate-500 mt-0.5">{totalCampaigns} campaign{totalCampaigns !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2.5 text-slate-500 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => { setForm({ name: '', subject: '', template_id: '', list_ids: [], from_name: 'EDUVA Team', from_email: 'noreply@app.myeduva.com', send_mode: 'now', scheduled_at: '', recurrence_type: 'none', recurrence_day: 1, recurrence_hour: 9 }); setCreateModal(true); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-sm font-semibold rounded-xl shadow-sm transition-all hover:shadow-blue-200 hover:shadow-lg"
            data-testid="new-campaign-btn">
            <Plus className="w-4 h-4" /> New Campaign
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">{[...Array(2)].map((_, i) => <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />)}</div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl py-20 text-center shadow-sm">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-slate-300" />
          </div>
          <p className="font-bold text-slate-700">No campaigns yet</p>
          <p className="text-sm text-slate-400 mt-1">Create your first campaign to reach your contacts</p>
          <button onClick={() => setCreateModal(true)} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#2563eb] text-white text-sm font-semibold rounded-xl hover:bg-[#1d4ed8] transition-colors">
            <Plus className="w-4 h-4" /> New Campaign
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map(c => {
            const openRate = c.sent > 0 ? ((c.opened / c.sent) * 100).toFixed(1) : '0.0';
            const clickRate = c.sent > 0 ? ((c.clicked / c.sent) * 100).toFixed(1) : '0.0';
            const deliveryRate = c.total > 0 ? (((c.sent - (c.failed || 0)) / c.total) * 100).toFixed(0) : 0;
            const isScheduled = c.status === 'scheduled';
            return (
              <div key={c.id}
                className={`bg-white border rounded-2xl p-6 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.06)] hover:shadow-[0_8px_16px_-4px_rgba(15,23,42,0.08)] transition-all ${isScheduled ? 'border-violet-200' : 'border-slate-200/80'}`}
                data-testid={`campaign-row-${c.id}`}>
                {/* Scheduled banner */}
                {isScheduled && c.scheduled_at && (
                  <div className="flex items-center gap-2 bg-violet-50 border border-violet-100 rounded-xl px-4 py-2.5 mb-4">
                    <CalendarClock className="w-4 h-4 text-violet-600 flex-shrink-0" />
                    <p className="text-sm text-violet-700 font-semibold">
                      Scheduled for{' '}
                      <span className="font-bold">{new Date(c.scheduled_at).toLocaleString([], { weekday:'short', month:'short', day:'numeric', year:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
                    </p>
                  </div>
                )}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
                      <p className="font-bold text-slate-900 text-base">{c.name}</p>
                      <StatusBadge status={c.status} scheduledAt={c.scheduled_at} />
                      <RecurrenceBadge type={c.recurrence_type} day={c.recurrence_day} hour={c.recurrence_hour} count={c.recurrence_count} paused={c.recurrence_paused} />
                      {c.note && <span className="text-xs text-amber-700 bg-amber-50 border border-amber-100 px-2.5 py-0.5 rounded-full font-semibold">Demo Mode</span>}
                    </div>
                    <p className="text-sm text-slate-500 mb-4">{c.subject}</p>
                    {/* Stats — hidden for scheduled/draft with no data */}
                    {!isScheduled && (
                      <div className="flex flex-wrap gap-6">
                        {[
                          { label: 'Total',   value: (c.total || 0).toLocaleString(), color: 'text-slate-700' },
                          { label: 'Sent',    value: (c.sent || 0).toLocaleString(), color: 'text-emerald-700' },
                          { label: 'Opened',  value: `${(c.opened || 0).toLocaleString()} (${openRate}%)`, color: 'text-blue-700' },
                          { label: 'Clicked', value: `${(c.clicked || 0).toLocaleString()} (${clickRate}%)`, color: 'text-purple-700' },
                          { label: 'Failed',  value: (c.failed || 0).toLocaleString(), color: 'text-red-600' },
                        ].map(({ label, value, color }) => (
                          <div key={label}>
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
                            <p className={`text-sm font-bold ${color}`}>{value}</p>
                          </div>
                        ))}
                        {c.total > 0 && (
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Delivery</p>
                            <div className="flex items-center gap-1.5">
                              <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${deliveryRate}%` }} />
                              </div>
                              <span className="text-xs font-bold text-emerald-700">{deliveryRate}%</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                    {/* Scheduled: Cancel + Send Now override */}
                    {isScheduled && (
                      <>
                        <button onClick={() => handleCancelSchedule(c)} disabled={cancelling[c.id]}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:border-red-200 hover:text-red-600 hover:bg-red-50 transition-all disabled:opacity-50"
                          data-testid={`cancel-schedule-${c.id}`}>
                          {cancelling[c.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />} Cancel
                        </button>
                        <button onClick={() => handleSend(c)} disabled={sending[c.id]}
                          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white text-sm font-bold rounded-xl hover:bg-violet-700 disabled:opacity-60 shadow-sm transition-all"
                          data-testid={`send-now-${c.id}`}>
                          {sending[c.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Send Now
                        </button>
                      </>
                    )}
                    {/* Draft: Send */}
                    {c.status === 'draft' && (
                      <button onClick={() => handleSend(c)} disabled={sending[c.id]}
                        className="flex items-center gap-2 px-4 py-2.5 bg-[#2563eb] text-white text-sm font-bold rounded-xl hover:bg-[#1d4ed8] disabled:opacity-60 shadow-sm transition-all hover:shadow-blue-200"
                        data-testid={`send-campaign-${c.id}`}>
                        {sending[c.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
                      </button>
                    )}
                    {/* Sending indicator */}
                    {c.status === 'sending' && (
                      <span className="flex items-center gap-2 text-blue-600 text-sm font-semibold px-4 py-2.5 bg-blue-50 rounded-xl border border-blue-100">
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                      </span>
                    )}
                    {/* Duplicate (all statuses except actively sending) */}
                    {c.status !== 'sending' && (
                      <button onClick={() => handleDuplicate(c)} disabled={duplicating[c.id]}
                        className="p-2.5 text-slate-400 hover:text-[#2563eb] border border-slate-200 rounded-xl hover:border-blue-200 hover:bg-blue-50 transition-all"
                        title="Duplicate campaign" data-testid={`duplicate-campaign-${c.id}`}>
                        {duplicating[c.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                      </button>
                    )}
                    {/* Pause / Resume recurrence */}
                    {c.recurrence_type && c.recurrence_type !== 'none' && (
                      <button onClick={() => handlePauseRecurrence(c)} disabled={pausing[c.id]}
                        className="p-2.5 text-slate-400 hover:text-violet-600 border border-slate-200 rounded-xl hover:border-violet-200 hover:bg-violet-50 transition-all"
                        title={c.recurrence_paused ? 'Resume recurrence' : 'Pause recurrence'}
                        data-testid={`pause-recurrence-${c.id}`}>
                        {pausing[c.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : c.recurrence_paused ? <PlayCircle className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />}
                      </button>
                    )}
                    <button onClick={() => handleDelete(c)} className="p-2.5 text-slate-400 hover:text-red-500 border border-slate-200 rounded-xl hover:border-red-200 hover:bg-red-50 transition-all">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="flex items-center justify-between mt-6 pt-5 border-t border-slate-100" data-testid="campaigns-pagination">
          <p className="text-sm text-slate-500">
            Page <span className="font-semibold text-slate-700">{currentPage}</span> of <span className="font-semibold text-slate-700">{totalPages}</span>
            <span className="hidden sm:inline"> · {totalCampaigns} campaign{totalCampaigns !== 1 ? 's' : ''} total</span>
          </p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="campaigns-prev-page"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            {Array.from({ length: totalPages }, (_, idx) => idx + 1)
              .filter(p => totalPages <= 7 || Math.abs(p - currentPage) <= 2 || p === 1 || p === totalPages)
              .map((p, i, arr) => {
                const prev = arr[i - 1];
                return (
                  <React.Fragment key={p}>
                    {prev && p - prev > 1 && (
                      <span className="px-1 text-slate-400 text-sm select-none">…</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(p)}
                      className={`w-9 h-9 rounded-xl text-sm font-semibold transition-all ${
                        p === currentPage
                          ? 'bg-[#2563eb] text-white shadow-sm'
                          : 'text-slate-600 border border-slate-200 hover:bg-slate-50'
                      }`}
                      data-testid={`campaigns-page-${p}`}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })
            }
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="campaigns-next-page"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      <Dialog open={createModal} onOpenChange={setCreateModal}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="font-bold text-slate-900">New Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4 max-h-[75vh] overflow-y-auto pr-0.5">
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">Campaign Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={INPUT} placeholder="e.g. March Newsletter" data-testid="campaign-name-input" /></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">Subject Line *</label>
              <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className={INPUT} placeholder="Email subject line" /></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">Template *</label>
              <select value={form.template_id} onChange={e => setForm(f => ({ ...f, template_id: e.target.value }))} className={INPUT + ' bg-white'}>
                <option value="">Select a template…</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">From Name</label>
              <input value={form.from_name} onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))} className={INPUT} placeholder="EDUVA Team" /></div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block uppercase tracking-wide">Contact Lists * <span className="text-slate-400 font-normal normal-case">(select one or more)</span></label>
              <div className="border border-slate-200 rounded-xl overflow-hidden max-h-36 overflow-y-auto">
                {lists.length === 0 ? <p className="p-4 text-sm text-slate-400">No lists yet. Create a contact list first.</p> :
                  lists.map(lst => (
                    <label key={lst.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0 transition-colors">
                      <input type="checkbox" checked={form.list_ids.includes(lst.id)} onChange={() => toggleListId(lst.id)} className="rounded accent-[#2563eb]" />
                      <span className="text-sm flex-1 font-medium text-slate-700">{lst.name}</span>
                      <span className="text-xs font-semibold text-slate-400">{(lst.contact_count || 0).toLocaleString()}</span>
                    </label>
                  ))
                }
              </div>
            </div>
            {form.list_ids.length > 0 && (
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-2.5 text-sm text-blue-700 flex items-center gap-2 font-semibold">
                <Users className="w-4 h-4" />
                {lists.filter(l => form.list_ids.includes(l.id)).reduce((s, l) => s + (l.contact_count || 0), 0).toLocaleString()} contacts selected
              </div>
            )}

            {/* ── When to send ─────────────────────────────────────────── */}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-2 block uppercase tracking-wide">When to Send</label>
              <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-xl">
                {[
                  { key: 'now', label: 'Send Now', Icon: Zap },
                  { key: 'schedule', label: 'Schedule', Icon: CalendarClock },
                ].map(({ key, label, Icon }) => (
                  <button key={key} type="button"
                    onClick={() => setForm(f => ({ ...f, send_mode: key }))}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                      form.send_mode === key
                        ? key === 'now' ? 'bg-white text-slate-900 shadow-sm' : 'bg-white text-violet-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid={`send-mode-${key}`}>
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>
              {form.send_mode === 'schedule' && (
                <div className="mt-3 space-y-1.5">
                  <label className="text-xs font-semibold text-slate-600 block">Date &amp; Time *</label>
                  <input
                    type="datetime-local"
                    value={form.scheduled_at}
                    min={minSchedule()}
                    onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))}
                    className={INPUT}
                    data-testid="schedule-datetime-input"
                  />
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Campaign sends automatically at chosen time
                  </p>
                </div>
              )}
            </div>

            {/* ── Recurrence ─────────────────────────────────────────── */}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-2 block uppercase tracking-wide">Repeat (Recurrence)</label>
              <div className="flex gap-2 flex-wrap mb-3">
                {[{key:'none',label:'No repeat'},{key:'weekly',label:'Weekly'},{key:'monthly',label:'Monthly'}].map(({key,label})=>(
                  <button key={key} type="button"
                    onClick={() => setForm(f => ({ ...f, recurrence_type: key }))}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold border transition-all ${
                      form.recurrence_type === key
                        ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                        : 'text-slate-500 border-slate-200 hover:border-violet-300 hover:text-violet-600'
                    }`}
                    data-testid={`recurrence-${key}`}>
                    {key !== 'none' && <Repeat className="w-3.5 h-3.5" />} {label}
                  </button>
                ))}
              </div>
              {form.recurrence_type !== 'none' && (
                <div className="bg-violet-50 border border-violet-100 rounded-xl p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
                        {form.recurrence_type === 'weekly' ? 'Day of Week' : 'Day of Month'}
                      </label>
                      {form.recurrence_type === 'weekly' ? (
                        <select value={form.recurrence_day} onChange={e => setForm(f => ({ ...f, recurrence_day: Number(e.target.value) }))}
                          className={INPUT + ' bg-white text-xs py-2'} data-testid="recurrence-day-select">
                          {DAYS_OF_WEEK.map((d,i) => <option key={i} value={i}>{d}</option>)}
                        </select>
                      ) : (
                        <select value={form.recurrence_day} onChange={e => setForm(f => ({ ...f, recurrence_day: Number(e.target.value) }))}
                          className={INPUT + ' bg-white text-xs py-2'} data-testid="recurrence-day-select">
                          {Array.from({length:28},(_,i)=><option key={i+1} value={i+1}>Day {i+1}</option>)}
                        </select>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Send Time (UTC)</label>
                      <select value={form.recurrence_hour} onChange={e => setForm(f => ({ ...f, recurrence_hour: Number(e.target.value) }))}
                        className={INPUT + ' bg-white text-xs py-2'} data-testid="recurrence-hour-select">
                        {HOURS.map((h,i) => <option key={i} value={i}>{h}</option>)}
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-violet-600 flex items-center gap-1.5">
                    <Repeat className="w-3 h-3 flex-shrink-0" />
                    After each send, the next one auto-schedules. Pause anytime from the campaign card.
                  </p>
                </div>
              )}
            </div>

            <button onClick={handleCreate} disabled={saving}
              className={`w-full text-white py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-all shadow-sm ${
                form.send_mode === 'schedule' ? 'bg-violet-600 hover:bg-violet-700' : 'bg-[#2563eb] hover:bg-[#1d4ed8]'
              }`}
              data-testid="create-campaign-btn">
              {saving
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : form.send_mode === 'schedule' ? <CalendarClock className="w-4 h-4" /> : <Check className="w-4 h-4" />}
              {form.send_mode === 'schedule' ? 'Schedule Campaign' : 'Create Campaign'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ANALYTICS TAB
// ══════════════════════════════════════════════════════════════════════════════
function DonutChart({ value, color, label }) {
  const data = [
    { name: label, value: value || 0 },
    { name: 'rest', value: Math.max(100 - (value || 0), 0) },
  ];
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: 80, height: 80 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={28} outerRadius={38} startAngle={90} endAngle={-270} dataKey="value" strokeWidth={0}>
              <Cell fill={color} />
              <Cell fill="#f1f5f9" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-black text-slate-900">{value}%</span>
        </div>
      </div>
      <p className="text-xs font-semibold text-slate-500">{label}</p>
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillCampaign, setDrillCampaign] = useState(null);
  const [drillData, setDrillData] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillPage, setDrillPage] = useState(1);
  const DRILL_PER_PAGE = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/marketing/analytics'); setData(r.data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDrilldown = async (campaign) => {
    setDrillCampaign(campaign);
    setDrillPage(1);
    setDrillData([]);
    setDrillLoading(true);
    try {
      const r = await api.get(`/marketing/campaigns/${campaign.id}/drilldown`);
      setDrillData(r.data || []);
    } catch { setDrillData([]); }
    finally { setDrillLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>;
  if (!data) return null;

  const deliveryRate = data.total_sent > 0 ? +(((data.total_sent - data.total_failed) / data.total_sent) * 100).toFixed(1) : 0;

  const kpis = [
    { icon: Send, label: 'Emails Sent', value: data.total_sent.toLocaleString(), sub: `${data.total_campaigns} campaigns`, iconBg: 'bg-blue-50', iconColor: 'text-[#2563eb]' },
    { icon: Inbox, label: 'Opened', value: data.total_opened.toLocaleString(), sub: `${data.open_rate}% open rate`, iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { icon: MousePointerClick, label: 'Clicked', value: data.total_clicked.toLocaleString(), sub: `${data.click_rate}% click rate`, iconBg: 'bg-purple-50', iconColor: 'text-purple-600' },
    { icon: AlertCircle, label: 'Failed', value: data.total_failed.toLocaleString(), sub: 'delivery failures', iconBg: 'bg-red-50', iconColor: 'text-red-500' },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(({ icon: Icon, label, value, sub, iconBg, iconColor }) => (
          <div key={label} className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.06)] hover:shadow-[0_8px_16px_-4px_rgba(15,23,42,0.08)] transition-all">
            <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center mb-4`}>
              <Icon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <p className="text-2xl font-extrabold text-slate-900 leading-none">{value}</p>
            <p className="text-sm font-semibold text-slate-600 mt-1">{label}</p>
            {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Rate Donuts */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.06)]">
          <p className="text-sm font-bold text-slate-900 mb-5">Email Rates</p>
          <div className="flex items-center justify-around">
            <DonutChart value={data.open_rate} color="#10B981" label="Open Rate" />
            <DonutChart value={data.click_rate} color="#7C3AED" label="Click Rate" />
            <DonutChart value={deliveryRate} color="#2563eb" label="Delivery" />
          </div>
        </div>

        {/* Audience Summary */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.06)]">
          <p className="text-sm font-bold text-slate-900 mb-5">Audience Summary</p>
          <div className="space-y-3">
            {[
              { label: 'Total Campaigns', value: data.total_campaigns, icon: Activity },
              { label: 'Contact Lists', value: data.total_lists, icon: Target },
              { label: 'Active Contacts', value: data.total_contacts.toLocaleString(), icon: Users },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0">
                <div className="flex items-center gap-2.5 text-sm text-slate-600">
                  <Icon className="w-4 h-4 text-slate-400" />
                  {label}
                </div>
                <span className="text-sm font-bold text-slate-900">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Deliverability Tips */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.06)]">
          <p className="text-sm font-bold text-slate-900 mb-5 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" />Deliverability Tips</p>
          <div className="space-y-2.5">
            {[
              { icon: Check, t: 'Use a verified Resend sender domain', ok: true },
              { icon: Check, t: 'List-Unsubscribe header auto-added', ok: true },
              { icon: Check, t: 'Keep subject lines under 50 characters', ok: true },
              { icon: AlertCircle, t: 'Add RESEND_API_KEY in .env to go live', ok: false },
            ].map(({ icon: Icon, t, ok }) => (
              <div key={t} className="flex items-start gap-2.5 text-xs">
                <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${ok ? 'text-emerald-500' : 'text-amber-500'}`} />
                <span className={ok ? 'text-slate-600' : 'text-amber-700 font-medium'}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Campaign Performance Table */}
      {data.campaigns?.length > 0 && (
        <div className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-[0_2px_8px_-2px_rgba(15,23,42,0.06)]">
          <div className="px-6 py-4 border-b border-slate-100">
            <p className="font-bold text-slate-900">Campaign Performance</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {['Campaign', 'Status', 'Sent', 'Opened', 'Clicked', 'Failed', 'Open Rate', 'Click Rate', ''].map(h => (
                    <th key={h} className="text-left px-5 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide border-b border-slate-100 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.campaigns.map(c => (
                  <tr key={c.id} className="hover:bg-slate-50/80 transition-colors cursor-pointer group" onClick={() => openDrilldown(c)} data-testid={`analytics-row-${c.id}`}>
                    <td className="px-5 py-3.5 font-semibold text-slate-800 max-w-48 truncate">{c.name}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={c.status} /></td>
                    <td className="px-5 py-3.5 text-slate-700 font-medium">{(c.sent || 0).toLocaleString()}</td>
                    <td className="px-5 py-3.5 text-emerald-700 font-bold">{(c.opened || 0).toLocaleString()}</td>
                    <td className="px-5 py-3.5 text-purple-700 font-bold">{(c.clicked || 0).toLocaleString()}</td>
                    <td className="px-5 py-3.5 text-red-600 font-medium">{(c.failed || 0).toLocaleString()}</td>
                    <td className="px-5 py-3.5 text-slate-600 font-semibold">{c.sent > 0 ? ((c.opened / c.sent) * 100).toFixed(1) : 0}%</td>
                    <td className="px-5 py-3.5 text-slate-600 font-semibold">{c.sent > 0 ? ((c.clicked / c.sent) * 100).toFixed(1) : 0}%</td>
                    <td className="px-5 py-3.5">
                      <span className="flex items-center gap-1 text-xs font-semibold text-[#2563eb] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        <Eye className="w-3.5 h-3.5" /> Details
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Drill-down Modal ─────────────────────────────────────────── */}
      <Dialog open={!!drillCampaign} onOpenChange={(open) => { if (!open) setDrillCampaign(null); }}>
        <DialogContent className="max-w-3xl rounded-3xl max-h-[85vh] flex flex-col">
          <DialogHeader className="pb-4 border-b border-slate-100 flex-shrink-0">
            <DialogTitle className="font-bold text-slate-900 flex items-center gap-2">
              <Target className="w-4 h-4 text-[#2563eb]" />
              {drillCampaign?.name} — Contact Breakdown
            </DialogTitle>
            {drillData.length > 0 && (
              <p className="text-sm text-slate-500 mt-1">
                {drillData.length} contact{drillData.length !== 1 ? 's' : ''} tracked ·
                {' '}<span className="text-emerald-600 font-semibold">{drillData.filter(d=>d.opened).length} opened</span> ·
                {' '}<span className="text-purple-600 font-semibold">{drillData.filter(d=>d.clicked).length} clicked</span> ·
                {drillData.filter(d=>d.bounced).length > 0 && <span className="text-red-600 font-semibold"> {drillData.filter(d=>d.bounced).length} bounced</span>}
              </p>
            )}
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pt-4 min-h-0">
            {drillLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
            ) : drillData.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <Activity className="w-6 h-6 text-slate-300" />
                </div>
                <p className="text-sm font-semibold text-slate-500">No engagement events yet</p>
                <p className="text-xs text-slate-400 mt-1">Events appear when contacts open, click, or interact with the email</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-2xl border border-slate-100">
                  <table className="w-full text-sm" data-testid="drilldown-table">
                    <thead className="bg-slate-50">
                      <tr>
                        {['Email', 'Delivered', 'Opened', 'Clicked', 'Bounced', 'Unsubscribed'].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide border-b border-slate-100 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {drillData.slice((drillPage-1)*DRILL_PER_PAGE, drillPage*DRILL_PER_PAGE).map((contact, i) => (
                        <tr key={i} className="hover:bg-slate-50/60 transition-colors">
                          <td className="px-4 py-3 font-medium text-slate-800 max-w-52 truncate text-xs">{contact.email}</td>
                          <td className="px-4 py-3">
                            {contact.delivered
                              ? <span className="flex items-center gap-1 text-emerald-600 text-xs font-semibold"><Check className="w-3.5 h-3.5" /> Yes</span>
                              : <span className="text-slate-300 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {contact.opened
                              ? <div><span className="flex items-center gap-1 text-emerald-600 text-xs font-semibold"><Inbox className="w-3.5 h-3.5" /> Yes</span>
                                {contact.first_open && <p className="text-[10px] text-slate-400 mt-0.5">{new Date(contact.first_open).toLocaleString([], {month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</p>}
                                </div>
                              : <span className="text-slate-300 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {contact.clicked
                              ? <div><span className="flex items-center gap-1 text-purple-600 text-xs font-semibold"><MousePointerClick className="w-3.5 h-3.5" /> Yes</span>
                                {contact.first_click && <p className="text-[10px] text-slate-400 mt-0.5">{new Date(contact.first_click).toLocaleString([], {month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</p>}
                                </div>
                              : <span className="text-slate-300 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {contact.bounced || contact.complained
                              ? <span className="flex items-center gap-1 text-red-500 text-xs font-semibold"><AlertCircle className="w-3.5 h-3.5" />{contact.complained ? 'Spam' : 'Bounce'}</span>
                              : <span className="text-slate-300 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {contact.unsubscribed
                              ? <span className="flex items-center gap-1 text-amber-600 text-xs font-semibold"><Ban className="w-3.5 h-3.5" /> Yes</span>
                              : <span className="text-slate-300 text-xs">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Drill-down pagination */}
                {drillData.length > DRILL_PER_PAGE && (
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-500">
                      Showing {((drillPage-1)*DRILL_PER_PAGE)+1}–{Math.min(drillPage*DRILL_PER_PAGE, drillData.length)} of {drillData.length}
                    </p>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setDrillPage(p=>Math.max(1,p-1))} disabled={drillPage===1}
                        className="px-3 py-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 transition-all flex items-center gap-1">
                        <ChevronLeft className="w-3 h-3" /> Prev
                      </button>
                      <span className="text-xs text-slate-500 px-2">Page {drillPage} of {Math.ceil(drillData.length/DRILL_PER_PAGE)}</span>
                      <button onClick={() => setDrillPage(p=>Math.min(Math.ceil(drillData.length/DRILL_PER_PAGE),p+1))} disabled={drillPage===Math.ceil(drillData.length/DRILL_PER_PAGE)}
                        className="px-3 py-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 transition-all flex items-center gap-1">
                        Next <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { key: 'campaigns', label: 'Campaigns', icon: Send },
  { key: 'templates', label: 'Templates', icon: Mail },
  { key: 'contacts',  label: 'Contacts',  icon: Users },
  { key: 'analytics', label: 'Analytics', icon: BarChart2 },
];

export default function MarketingPage() {
  const [tab, setTab] = useState('campaigns');

  return (
    <div className="px-6 md:px-8 py-6 md:py-8">
      {/* Premium Page Header */}
      <div className="relative bg-[#0f172a] rounded-3xl overflow-hidden mb-8" style={{ minHeight: 140 }}>
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.5) 1px,transparent 1px)`,
          backgroundSize: '24px 24px'
        }} />
        {/* Gradient orbs */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-32 w-48 h-48 bg-purple-600/10 rounded-full blur-2xl translate-y-1/2" />

        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-7">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-xl bg-[#2563eb]/20 flex items-center justify-center border border-[#2563eb]/30">
                <Mail className="w-5 h-5 text-blue-400" />
              </div>
              <h1 className="text-2xl font-extrabold text-white tracking-tight">Email Marketing</h1>
            </div>
            <p className="text-sm text-slate-400">Send beautiful inbox-delivered campaigns to thousands of contacts</p>
          </div>
          {/* Setup banner — only shown when RESEND key not yet confirmed */}
          <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-2.5 flex-shrink-0">
            <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <div>
              <p className="text-xs font-bold text-emerald-300 leading-none">Live</p>
              <p className="text-xs text-emerald-400/80 mt-0.5">Sending from <code className="font-mono bg-emerald-500/10 px-1 rounded">app.myeduva.com</code></p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-2xl w-fit mb-8" data-testid="marketing-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              tab === key
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-800'
            }`}
            data-testid={`tab-${key}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'campaigns' && <CampaignsTab />}
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'contacts'  && <ContactsTab />}
      {tab === 'analytics' && <AnalyticsTab />}
    </div>
  );
}
